import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { axiosPrivate } from "../../api/axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Typography,
  useTheme,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Popover,
  CardMedia,
  CardActions,
  Divider
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import { Icon } from "../icons";

const systemImageMapping = {
  DT: '/images/menu/4.png',
  APS: '/images/menu/1.png',
  SAFE: '/images/menu/2.png',
  MASTER: '/images/menu/5.png',
  PLAN: '/images/menu/3.png',
};

const systemGifMapping = {
  DT: '/images/gif/DT.gif',
  APS: '/images/gif/APS.gif',
  SAFE: '/images/gif/SAFE.gif',
  MASTER: '/images/gif/MASTER.gif',
  PLAN: '/images/gif/APS.gif',
};

const GroupsPage = () => {
  const [groupInfo, setGroupInfo] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentSystem, setCurrentSystem] = useState(null);
  const [systemEdit, setSystemEdit] = useState({
    system_name: '',
    system_description: ''
  });
  const [newProject, setNewProject] = useState({
    project_name: '',
    project_code: '',
    systems: ['DT', 'APS', 'SAFE'] // 기본적으로 모든 시스템 활성화
  });
  const [popover, setPopover] = useState({
    anchorEl: null,
    system: null,
    project: null,
  });
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [groupEdit, setGroupEdit] = useState({ group_name: '', group_code: '' });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    type: '', // 'group' or 'project'
    target: null,
    message: ''
  });
  const [error, setError] = useState('');
  const theme = useTheme();

  const user = useSelector((state) => state.auth.user);
  const isRoot = user?.is_root === 1;

  useEffect(() => {
    fetchAllGroupInfo();
  }, []);

  const fetchAllGroupInfo = () => {
    axiosPrivate
      .post("/admin/getAllGroupInfo")
      .then((res) => {
        const groupData = res.data.data || [];
        setGroupInfo(groupData);
        
        // 초기 확장 상태 설정 - 모두 접힌 상태로
        setExpandedGroups({});
        setExpandedProjects({});
      })
      .catch((err) => {
        console.error("그룹 정보 가져오기 실패:", err);
      });
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // 기준 데이터 관리 페이지 이동
  const handleMasterData = (groupCode, projectCode, projectName) => {
    const encodedName = encodeURIComponent(projectName);
    const url = `/projects/${groupCode}/${projectCode}/master?name=${encodedName}`;
    window.location.href = url;
  };

  // 시스템 열기 처리 함수들
  const handleViewItw = (groupCode, projectCode) => {
    const url = `${window.location.origin}/insightwin/${groupCode}/${projectCode}`;
    const windowName = `insightwin_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewSafe = (groupCode, projectCode) => {
    const url = `${window.location.origin}/safe/${groupCode}/${projectCode}`;
    const windowName = `safe_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewAps = (groupCode, projectCode) => {
    const url = `${window.location.origin}/aps/${groupCode}/${projectCode}/plan`;
    const windowName = `aps_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewPlan = (groupCode, projectCode) => {
    const url = `${window.location.origin}/plan/${groupCode}/${projectCode}`;
    const windowName = `plan_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  // 시스템 편집 처리
  const handleEditItw = (groupCode, projectCode, groupName, projectName, projectId) => {
    const url = `${window.location.origin}/edit/file/${groupCode}/${projectCode}?projectName=${projectName}&groupName=${groupName}&pid=${projectId}`;
    const windowName = `edit_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  const handleViewEditItw = (groupCode, projectCode) => {
    const url = `${window.location.origin}/insightwinLEDT/${groupCode}/${projectCode}`;
    const windowName = `edit_ledt_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  // 레이아웃 테스트 편집 처리
  const handleViewLayoutTest = (groupCode, projectCode, projectId) => {
    const url = `${window.location.origin}/insightwinLE/${groupCode}/${projectCode}?pid=${projectId}`;
    const windowName = `layout_${groupCode}_${projectCode}`;
    window.open(url, windowName);
  };

  // 시스템 정보 편집 다이얼로그 열기
  const handleOpenEditDialog = (system) => {
    setCurrentSystem(system);
    setSystemEdit({
      system_name: system.system_name || '',
      system_description: system.system_description || ''
    });
    setEditDialogOpen(true);
    setError('');
  };

  // 시스템 정보 편집 다이얼로그 닫기
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentSystem(null);
  };

  // 시스템 정보 업데이트
  const handleUpdateSystem = () => {
    if (!currentSystem) return;

    axiosPrivate
      .post("/project/updateSystemInfo", {
        projectSystemId: currentSystem.project_system_id,
        systemName: systemEdit.system_name,
        systemDescription: systemEdit.system_description
      })
      .then((res) => {
        if (res.data.success) {
          handleCloseEditDialog();
          fetchAllGroupInfo(); // 목록 새로고침
        }
      })
      .catch((err) => {
        console.error("시스템 정보 업데이트 실패:", err);
        setError(err.response?.data?.message || '시스템 정보 업데이트 중 오류가 발생했습니다.');
      });
  };

  const handleOpenEditGroupDialog = (group) => {
    setCurrentGroup(group);
    setGroupEdit({ group_name: group.group_name, group_code: group.group_code });
    setEditGroupDialogOpen(true);
  };

  const handleCloseEditGroupDialog = () => {
    setEditGroupDialogOpen(false);
    setCurrentGroup(null);
    setGroupEdit({ group_name: '', group_code: '' });
  };

  const handleUpdateGroup = () => {
    if (!currentGroup) return;
    axiosPrivate.post('/admin/updateGroup', {
      groupId: currentGroup.group_id,
      ...groupEdit
    }).then(() => {
      handleCloseEditGroupDialog();
      fetchAllGroupInfo();
    }).catch(err => {
      console.error('그룹 정보 업데이트 실패:', err);
      setError(err.response?.data?.message || '그룹 정보 업데이트 중 오류가 발생했습니다.');
    });
  };

  const handleOpenDeleteConfirm = (target, type) => {
    const message = type === 'group'
      ? `그룹 '${target.group_name}'을(를) 삭제하시겠습니까? 그룹에 속한 프로젝트가 없어야 삭제 가능합니다.`
      : `프로젝트 '${target.project_name}'을(를) 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`;
    setConfirmDelete({ open: true, type, target, message });
  };

  const handleCloseDeleteConfirm = () => {
    setConfirmDelete({ open: false, type: '', target: null, message: '' });
  };

  const handleDelete = () => {
    const { type, target } = confirmDelete;
    if (!target) return;

    const url = type === 'group' ? '/admin/deleteGroup' : '/admin/deleteProject';
    const id = type === 'group' ? target.group_id : target.project_id;
    const key = type === 'group' ? 'groupId' : 'projectId';

    axiosPrivate.post(url, { [key]: id })
      .then(() => {
        handleCloseDeleteConfirm();
        fetchAllGroupInfo();
      })
      .catch(err => {
        console.error(`${type} 삭제 실패:`, err);
        setError(err.response?.data?.message || `${type} 삭제 중 오류가 발생했습니다.`);
        // Keep dialog open to show error if needed, or handle error display differently
      });
  };

  // 새 프로젝트 생성 다이얼로그 열기
  const handleOpenCreateDialog = (group) => {
    setCurrentGroup(group);
    setCreateDialogOpen(true);
    setError('');
  };

  // 새 프로젝트 생성 다이얼로그 닫기
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewProject({
      project_name: '',
      project_code: '',
      systems: ['DT', 'APS', 'SAFE']
    });
  };

  // 새 프로젝트 입력값 변경 처리
  const handleNewProjectChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 시스템 정보 입력값 변경 처리
  const handleSystemEditChange = (e) => {
    const { name, value } = e.target;
    setSystemEdit(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 시스템 체크박스 변경 처리
  const handleSystemCheckChange = (systemType) => {
    setNewProject(prev => {
      const systems = [...prev.systems];
      if (systems.includes(systemType)) {
        return { ...prev, systems: systems.filter(s => s !== systemType) };
      } else {
        return { ...prev, systems: [...systems, systemType] };
      }
    });
  };

  // 새 프로젝트 생성 제출
  const handleCreateProject = () => {
    if (!newProject.project_name || !newProject.project_code || !currentGroup) {
      setError('프로젝트 이름, 코드, 그리고 그룹 정보는 필수입니다.');
      return;
    }

    if (newProject.systems.length === 0) {
      setError('최소 하나 이상의 시스템을 선택해야 합니다.');
      return;
    }

    axiosPrivate
      .post("/admin/addNewProjectToGroup", { ...newProject, group_id: currentGroup.group_id })
      .then((res) => {
        handleCloseCreateDialog();
        fetchAllGroupInfo(); // 목록 새로고침
      })
      .catch((err) => {
        console.error("프로젝트 생성 실패:", err);
        setError(err.response?.data?.message || '프로젝트 생성 중 오류가 발생했습니다.');
      });
  };

  const handlePopoverOpen = (event, system, project) => {
    setPopover({
      anchorEl: event.currentTarget,
      system: system,
      project: project
    });
  };

  const handlePopoverClose = () => {
    setPopover({ anchorEl: null, system: null, project: null });
  };

  const renderGroupCard = (group) => (
    <Grid size={{ xs: 12 }} key={group.group_id}>
      <Card sx={{ mb: 4, overflow: 'visible' }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {group.group_name}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              {group.group_code}
            </Typography>
          }
          action={
            <Box>
              <IconButton onClick={(e) => { e.stopPropagation(); /* Handle user management */ }}>
                <GroupIcon />
              </IconButton>
              <IconButton onClick={(e) => { e.stopPropagation(); handleOpenEditGroupDialog(group); }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(group, 'group'); }}>
                <DeleteIcon />
              </IconButton>
              <IconButton onClick={(e) => { e.stopPropagation(); toggleGroupExpand(group.group_id); }}>
                {expandedGroups[group.group_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          }
          sx={{ 
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            cursor: 'pointer'
          }}
          onClick={() => toggleGroupExpand(group.group_id)}
        />
        <Collapse in={expandedGroups[group.group_id]}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            {group.projects && group.projects.length > 0 ? (
              <Grid container spacing={4}>
                {group.projects.map((project, index) => (
                  <Grid size={{ xs: 12 }} key={project.project_id} sx={index === 0 ? { mt: '1rem' } : {}}>
                    {renderProjectCard(project)}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography sx={{ textAlign: 'center', p: 4 }}>
                이 그룹에는 프로젝트가 없습니다.
              </Typography>
            )}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenCreateDialog(group)}
              >
                새 프로젝트 추가
              </Button>
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    </Grid>
  );

  const renderProjectCard = (project) => (
    <Box sx={{ 
      border: `1px solid ${theme.palette.divider}`, 
      borderRadius: "0.375rem", 
      bgcolor: 'var(--mui-palette-background-paper)',
    }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2.5,
          cursor: 'pointer',
          borderBottom: expandedProjects[project.project_id] ? `1px solid ${theme.palette.divider}` : 'none',
          '&:hover': {
            bgcolor: theme.palette.action.hover
          }
        }}
        onClick={() => toggleProjectExpand(project.project_id)}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {project.project_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {project.project_code}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={(e) => { e.stopPropagation(); /* Handle project management dialog */ }}
            sx={{ mr: 1 }}
          >
            관리
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(project, 'project'); }}
          >
            삭제
          </Button>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleProjectExpand(project.project_id); }}>
            {expandedProjects[project.project_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expandedProjects[project.project_id]}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {project.systems && project.systems.map(system => renderSystemItem(project, system))}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );

  const renderSystemItem = (project, system) => {
    const imageUrl = systemImageMapping[system.type_name] || '/images/menu/default.png';
    
    return (
      <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }} key={system.project_system_id} sx={{ textAlign: 'center' }}>
        <Box
          onClick={(e) => handlePopoverOpen(e, system, project)}
          sx={{
            p: 2,
            borderRadius: '12px',
            flexDirection: 'column',
            opacity: system.is_active ? 1 : 0.6,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[4]
            }
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={system.type_name}
            sx={{
              width: '100%',
              maxWidth: '120px',
              height: 'auto',
              mb: 1,
              borderRadius: '8px',
              boxShadow: theme.shadows[2]
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {system.system_name || system.type_name}
            </Typography>
            {!system.is_active && (
              <Chip label="비활성화" size="small" color="secondary" />
            )}
          </Box>
        </Box>
      </Grid>
    );
  };

  // 시스템 작업 팝오버
  const renderSystemPopover = () => {
    const { anchorEl, system, project } = popover;
    const open = Boolean(anchorEl);

    if (!system || !project) {
      return null;
    }

    const systemPermissionLevel = isRoot ? 2 : (system.system_permission_level || 0);
    const canEditSystem = isRoot || systemPermissionLevel >= 2;

    const handleActionClick = (action) => {
      handlePopoverClose();
      if (action) action();
    };
    
    const systemGif = systemGifMapping[system.type_name];

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
        }}
        PaperProps={{
            sx: {
                borderRadius: '16px',
                boxShadow: theme.shadows[8],
                width: 300,
                backgroundImage: 'none'
            }
        }}
      >
        <Card>
          {systemGif && (
            <CardMedia 
                component="img"
                sx={{ width: '100%', height: 'auto' }} 
                image={systemGif}
                alt={`${system.type_name} animation`}
            />
          )}
          <CardContent sx={{ p: 4, pt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
              <Chip
                  label={system.type_name}
                  variant='tonal'
                  size='small'
                  color={system.color || 'default'}
              />
              <Box>
                {system.type_name === 'DT' && (
                  <IconButton
                    size="small"
                    onClick={() => handleActionClick(() => handleEditItw(project.group_code, project.project_code, project.group_name, project.project_name, project.project_id))}
                    disabled={!system.is_active}
                  >
                    <Icon name="databaseIcon" style={{ width: '20px', height: '20px' }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => handleActionClick(() => handleOpenEditDialog(system))}
                  disabled={!system.is_active}
                >
                  <Icon name="editIcon" style={{ width: '20px', height: '20px' }} />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='h5'>
                  {system.system_name || system.type_name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', minHeight: '40px' }}>
                  {system.system_description || '설명이 없습니다.'}
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 4, pt: 0, gap: 1 }}>
            {system.is_active ? (
              <>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleActionClick(() => {
                    if (system.type_name === "DT") handleViewEditItw(project.group_code, project.project_code);
                    else if (system.type_name === "APS") handleViewAps(project.group_code, project.project_code);
                    else if (system.type_name === "SAFE") handleViewSafe(project.group_code, project.project_code);
                    else if (system.type_name === "MASTER") handleMasterData(project.group_code, project.project_code, project.project_name);
                    else if (system.type_name === "PLAN") handleViewPlan(project.group_code, project.project_code);
                  })}
                  disabled={!canEditSystem && system.type_name === "MASTER"}
                  startIcon={<OpenInNewIcon />}
                >
                  열기
                </Button>
                
                {system.type_name === "DT" && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleActionClick(() => handleViewLayoutTest(project.group_code, project.project_code, project.project_id))}
                    disabled={!canEditSystem}
                    startIcon={<EditAttributesIcon />}
                    sx={{
                      margin: '0 !important',
                    }}
                  >
                    레이아웃 편집
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                fullWidth
                disabled
                color="secondary"
              >
                비활성화
              </Button>
            )}
          </CardActions>
        </Card>
      </Popover>
    );
  };

  // 새 프로젝트 생성 다이얼로그
  const renderCreateProjectDialog = () => {
    return (
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>새 프로젝트 생성</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            autoFocus
            margin="dense"
            name="project_name"
            label="프로젝트 이름"
            type="text"
            fullWidth
            variant="outlined"
            value={newProject.project_name}
            onChange={handleNewProjectChange}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            name="project_code"
            label="프로젝트 코드"
            type="text"
            fullWidth
            variant="outlined"
            value={newProject.project_code}
            onChange={handleNewProjectChange}
            helperText="영문, 숫자, 언더스코어(_)만 사용 가능합니다"
            sx={{ mb: 3 }}
          />
          
          <Typography variant="subtitle1" sx={{ mb: 1 }}>활성화할 시스템</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={newProject.systems.includes('DT')}
                  onChange={() => handleSystemCheckChange('DT')}
                />
              }
              label="디지털트윈 (DT)"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={newProject.systems.includes('APS')}
                  onChange={() => handleSystemCheckChange('APS')}
                />
              }
              label="생산계획 (APS)"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={newProject.systems.includes('SAFE')}
                  onChange={() => handleSystemCheckChange('SAFE')}
                />
              }
              label="안전관리 (SAFE)"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>취소</Button>
          <Button onClick={handleCreateProject} variant="contained">생성</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderEditGroupDialog = () => {
    if (!currentGroup) return null;
    return (
      <Dialog open={editGroupDialogOpen} onClose={handleCloseEditGroupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>그룹 정보 편집</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="그룹 이름"
            type="text"
            fullWidth
            variant="outlined"
            value={groupEdit.group_name}
            onChange={(e) => setGroupEdit(prev => ({ ...prev, group_name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="그룹 코드"
            type="text"
            fullWidth
            variant="outlined"
            value={groupEdit.group_code}
            onChange={(e) => setGroupEdit(prev => ({ ...prev, group_code: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditGroupDialog}>취소</Button>
          <Button onClick={handleUpdateGroup} variant="contained">저장</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const renderConfirmDeleteDialog = () => (
    <Dialog open={confirmDelete.open} onClose={handleCloseDeleteConfirm}>
      <DialogTitle>삭제 확인</DialogTitle>
      <DialogContent>
        <Typography>{confirmDelete.message}</Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDeleteConfirm}>취소</Button>
        <Button onClick={handleDelete} color="error" variant="contained">삭제</Button>
      </DialogActions>
    </Dialog>
  );

  // 시스템 정보 편집 다이얼로그
  const renderEditSystemDialog = () => {
    return (
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>시스템 정보 편집</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            autoFocus
            margin="dense"
            name="system_name"
            label="시스템 이름"
            type="text"
            fullWidth
            variant="outlined"
            value={systemEdit.system_name}
            onChange={handleSystemEditChange}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            name="system_description"
            label="시스템 설명"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={systemEdit.system_description}
            onChange={handleSystemEditChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>취소</Button>
          <Button onClick={handleUpdateSystem} variant="contained">저장</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title={`그룹 및 프로젝트 관리`}
            sx={{
              '& .MuiCardHeader-title': {
                fontSize: '1.2rem',
                fontWeight: 600
              }
            }}
            action={
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenCreateDialog(null)}
              >
                새 그룹
              </Button>
            }
          />
          <CardContent sx={{ flexGrow: 1, overflow: "auto", px: 6 }}>
            {groupInfo.length > 0 ? (
              <Grid container>
                {groupInfo.map(renderGroupCard)}
              </Grid>
            ) : (
              <Typography sx={{ textAlign: 'center' }}>사용 가능한 그룹이 없습니다.</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      {renderCreateProjectDialog()}
      {renderEditSystemDialog()}
      {renderSystemPopover()}
      {renderEditGroupDialog()}
      {renderConfirmDeleteDialog()}
    </Grid>
  );
};

export default GroupsPage;
